import { useDebouncedValue, useLocalStorage } from '@mantine/hooks'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Label } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import {
  deleteReferralCodeFromLocalStorage,
  getReferralCodeFromLocalStorage,
  REFERRAL_CODE_FOR_REG_KEY,
} from 'src/components/UserReferral/utils'
import UITextFieldWithIcon, { UIInput } from 'src/components/ui/TextField'
import { executeUpdateMyInfo } from 'src/services/apollo/auth'
import { executeSetReferrer } from 'src/services/apollo/referral'
import { fetchCheckUsername } from 'src/services/apollo/user'
import { track } from 'src/utils/tracker'
import { twMerge } from 'tailwind-merge'
import { match, P } from 'ts-pattern'
import FluentSpinnerIos16Filled from '~icons/fluent/spinner-ios-16-filled'
import IcBaselineAlternateEmail from '~icons/ic/baseline-alternate-email'
import IcRoundCheckCircleOutline from '~icons/ic/round-check-circle-outline'
import MajesticonsExclamationCircleLine from '~icons/majesticons/exclamation-circle-line'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { NewUserOnboardingFormStep } from '../NewUserOnboardingFormStep'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'

const SUGGESTED_USERNAME_LIMIT = 2
const SUGGESTED_USERNAME_SHOW_MORE_LIMIT = 5

function SuggestedUsername(props: {
  suggestions: string[]
  onApply: (value: string) => void
}) {
  const { t } = useTranslation(['common', 'user'])
  const [showAll, setShowAll] = useState(false)
  const shownSuggestions = showAll
    ? props.suggestions.slice(0, SUGGESTED_USERNAME_SHOW_MORE_LIMIT)
    : props.suggestions.slice(0, SUGGESTED_USERNAME_LIMIT)
  return (
    <p className="text-sm/6">
      {t('user:onboarding.username.suggestion.title')}
      {shownSuggestions.map((suggestion, index) => (
        <React.Fragment key={suggestion}>
          {index !== 0 ? ', ' : ' '}
          <button
            className="inline underline"
            onClick={() => props.onApply(suggestion)}
          >
            @{suggestion}
          </button>
        </React.Fragment>
      ))}
      {!showAll && props.suggestions.length > SUGGESTED_USERNAME_LIMIT ? (
        <>
          {', '}
          <button className="inline underline" onClick={() => setShowAll(true)}>
            {t('user:onboarding.username.suggestion.show-all')}
          </button>
        </>
      ) : null}
    </p>
  )
}

type UsernameValidationState =
  | 'loading'
  | 'valid'
  | 'taken'
  | 'invalid'
  | 'prohibited'

function UsernameInput(props: {
  onChange: (value: string) => void
  value?: string
  disabled?: boolean
  validationState?: UsernameValidationState
}) {
  const { t } = useTranslation(['common', 'user'])
  const endIcon = useMemo(
    () =>
      match(props.validationState)
        .with('loading', () => (
          <FluentSpinnerIos16Filled key="end" className="animate-spin" />
        ))
        .with('valid', () => (
          <IcRoundCheckCircleOutline key="end" className="text-green-500" />
        ))
        .with(P.union('taken', 'invalid', 'prohibited'), () => (
          <MajesticonsExclamationCircleLine
            key="end"
            className="text-red-500"
          />
        ))
        .otherwise(() => undefined),
    [props.validationState],
  )

  const additionalInfo = match(props.validationState)
    .with('valid', () => (
      <p className="text-xs text-green-500">
        {t('user:onboarding.username.status.valid')}
      </p>
    ))
    .with('taken', () => (
      <p className="text-xs text-red-500">
        {t('user:onboarding.username.status.taken')}
      </p>
    ))
    .with('invalid', () => (
      <p className="text-xs text-red-500">
        {t('user:onboarding.username.status.invalid')}
      </p>
    ))
    .with('prohibited', () => (
      <p className="text-xs text-red-500">
        {t('user:onboarding.username.status.prohibited')}
      </p>
    ))
    .otherwise(() => undefined)

  const { getSuggestUsernamesRes } = useContext(NewUserOnboardingContext)
  const { data: suggestedUsernames } = getSuggestUsernamesRes ?? {}

  return (
    <div className="flex flex-col gap-2 mb-4">
      <Label htmlFor="onboarding-username">
        {t('user:onboarding.username.label')}
      </Label>
      <UITextFieldWithIcon
        id="onboarding-username"
        disabled={props.disabled}
        onChange={event => props.onChange(event.target.value)}
        wrapperProps={{
          className: twMerge(
            'border bg-white dark:bg-zinc-900 text-gray-800 dark:text-zinc-200 border-gray-200 dark:border-zinc-700 outline-none ring-0 focus:ring-1 focus:ring-zinc-700 focus:dark:ring-zinc-400 rounded-lg py-2 px-4 placeholder:text-neutral-400 placeholder:dark:text-neutral-500',
            props.validationState === 'taken' ||
              props.validationState === 'invalid' ||
              props.validationState === 'prohibited'
              ? 'ring-1 ring-red-500'
              : '',
            props.validationState === 'valid' ? 'ring-1 ring-green-500' : '',
          ),
        }}
        className="bg-white dark:bg-zinc-900"
        value={props.value}
        placeholder={t('user:onboarding.username.placeholder')}
        icon={[
          <IcBaselineAlternateEmail key="start" className="text-neutral-400" />,
          endIcon,
        ]}
        minLength={1}
        maxLength={20}
        required
      />
      {additionalInfo}
      {(suggestedUsernames?.length ?? 0 > 0) ? (
        <SuggestedUsername
          key={props.value}
          suggestions={suggestedUsernames?.slice(1) ?? []}
          onApply={props.onChange}
        />
      ) : null}
    </div>
  )
}

function ReferralSection() {
  const { t } = useTranslation(['common', 'user'])
  const { getMyReferrerIdRes, actionBusy } = useContext(
    NewUserOnboardingContext,
  )
  const { data: referrerId, loading: loadingReferrerId } =
    getMyReferrerIdRes ?? {}
  const allowManualInput = !loadingReferrerId && referrerId === null
  const [referrerCode, setReferrerCode] = useLocalStorage({
    key: REFERRAL_CODE_FOR_REG_KEY,
    /**
     * NOTE: Explicitly skipping serializing here
     * To match the behavior in packages/pixai-app/src/components/UserReferral/utils.ts
     * */
    serialize: v => v,
    deserialize: v => v ?? '',
  })
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="onboarding-referral">
        {t('user:onboarding.referral.label')}
        {allowManualInput ? (
          <span className="text-neutral-500">
            {' '}
            {t('user:onboarding.referral.optional')}
          </span>
        ) : null}
      </Label>
      {loadingReferrerId ? (
        <FluentSpinnerIos16Filled className="animate-spin" />
      ) : null}
      {allowManualInput ? (
        <UIInput
          id="onboarding-referral"
          onChange={event => setReferrerCode(event.target.value)}
          value={referrerCode}
          disabled={actionBusy}
          variant="rounded-normal"
          minLength={1}
          placeholder={t('user:onboarding.referral.placeholder')}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          autoComplete="off"
          inputMode="text"
          type="text"
        />
      ) : null}
      {referrerId ? (
        <p className="text-sm">
          {referrerCode ? (
            <span className="font-mono tracking-wide">{referrerCode}</span>
          ) : (
            t('common:unknown')
          )}
        </p>
      ) : null}
    </div>
  )
}

export function NewUserUsernameStep() {
  const { t } = useTranslation(['common', 'user'])
  const form = useNewUserOnboardingFormContext()
  const { username } = form.values
  const { userInfoRes } = useContext(NewUserOnboardingContext)
  const { data: userInfo, loading: userInfoLoading } = userInfoRes ?? {}

  const usernameHasntChanged = username === userInfo?.username
  const [debouncedUsername] = useDebouncedValue(username, 500)
  const usernameValidState = useAsync(async () => {
    if (usernameHasntChanged) return { valid: true }
    if (!debouncedUsername) return undefined
    if (!/^[a-z]/.test(debouncedUsername))
      return { valid: false, errorType: 'invalid' }
    if (!/^[a-z0-9-]+$/.test(debouncedUsername))
      return { valid: false, errorType: 'invalid' }
    const res = await fetchCheckUsername({ username: debouncedUsername })
    const result = res.data?.checkUsername
    if (result?.valid) return { valid: true }
    switch (result?.reason) {
      case 'taken':
        return { valid: false, errorType: 'taken' as const }
      case 'prohibited':
        return { valid: false, errorType: 'prohibited' as const }
      default:
        return { valid: false, errorType: 'invalid' as const }
    }
  }, [debouncedUsername, userInfo?.id, usernameHasntChanged])

  const usernameValidLoading =
    username !== debouncedUsername || usernameValidState.loading
  const usernameNotChanged = username === userInfo?.username

  let usernameValidationState: UsernameValidationState | undefined = undefined
  if (usernameValidLoading && !usernameNotChanged) {
    usernameValidationState = 'loading'
  } else if (usernameValidState.value?.valid === true) {
    usernameValidationState = 'valid'
  } else if (usernameValidState.value?.valid === false) {
    if (usernameValidState.value.errorType === 'taken') {
      usernameValidationState = 'taken'
    } else if (usernameValidState.value.errorType === 'prohibited') {
      usernameValidationState = 'prohibited'
    } else {
      usernameValidationState = 'invalid'
    }
  }

  const { getMyReferrerIdRes, actionBusy } = useContext(
    NewUserOnboardingContext,
  )
  const { data: referrerId } = getMyReferrerIdRes ?? {}

  const submitUsernameSection = useCallback(async () => {
    const referrerCode = getReferralCodeFromLocalStorage()
    track('started_choice', {
      referrerCode,
    })
    await Promise.all([
      executeUpdateMyInfo({
        input: {
          username: debouncedUsername,
        },
      }),
      // Don't submit referrerCode if referrerId already exists
      referrerCode && !referrerId
        ? executeSetReferrer({
            code: referrerCode,
          }).then(() => {
            // Mutate referrerId after submitted
            // To avoid submitting again on username update failure
            refetchReferrerId()
          })
        : Promise.resolve(),
    ])
    deleteReferralCodeFromLocalStorage()
  }, [debouncedUsername, referrerId])

  return (
    <NewUserOnboardingFormStep
      disableNext={usernameValidationState !== 'valid'}
      action={submitUsernameSection}
    >
      <h2 className="text-xl font-bold mb-4">
        {t('user:onboarding.username.title')}
      </h2>
      <h3 className="text-lg font-semibold mb-2">
        {t('user:onboarding.username.subtitle')}
      </h3>
      <p className="text-neutral-600 dark:text-neutral-400 mb-6">
        {t('user:onboarding.username.info')}
      </p>
      <UsernameInput
        disabled={(!username && userInfoLoading) || actionBusy}
        validationState={usernameValidationState}
        {...form.getInputProps('username')}
      />
      <ReferralSection />
    </NewUserOnboardingFormStep>
  )
}
function refetchReferrerId() {
  throw new Error('Function not implemented.')
}
